import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Play, AlertCircle } from 'lucide-react';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { useTimecardReportFilters } from '../hooks/useTimecardReportFilters';
import TimecardReportFiltersPanel from '../components/TimecardReportFiltersPanel';
import {
  formatWeekRangeLabel,
  type TimecardAnalysisFilterParams,
} from '../utils/timecardAnalysisReport';

export interface TimecardsAnalysisReportRequest {
  reportType: 'timecards-analysis';
  parameters: TimecardAnalysisFilterParams;
}

interface TimecardsAnalysisProps {
  onGenerateReport: (request: TimecardsAnalysisReportRequest) => void;
}

const TimecardsAnalysis: React.FC<TimecardsAnalysisProps> = ({ onGenerateReport }) => {
  const weekStartsOn = useWeekStartDay();
  const [fromWeekStart, setFromWeekStart] = useState('');
  const [toWeekStart, setToWeekStart] = useState('');

  const {
    statusFilter,
    setStatusFilter,
    selectedEmployeeIds,
    setSelectedEmployeeIds,
    selectedScheduleIds,
    setSelectedScheduleIds,
    selectedPositionIds,
    setSelectedPositionIds,
    employees,
    schedules,
    positions,
    loadingOptions,
    buildCommonFilters,
  } = useTimecardReportFilters();

  const getCurrentWeekStart = useCallback(() => {
    const today = new Date();
    const day = today.getDay();
    let diff = day - weekStartsOn;
    if (diff < 0) diff += 7;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - diff);
    return weekStart.toISOString().split('T')[0];
  }, [weekStartsOn]);

  const getWeekStartFromDate = useCallback(
    (dateString: string) => {
      const date = new Date(dateString);
      const day = date.getDay();
      let diff = day - weekStartsOn;
      if (diff < 0) diff += 7;
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - diff);
      return weekStart.toISOString().split('T')[0];
    },
    [weekStartsOn]
  );

  useEffect(() => {
    if (!fromWeekStart && weekStartsOn !== undefined) {
      const currentWeek = getCurrentWeekStart();
      setToWeekStart(currentWeek);

      const fourWeeksAgo = new Date();
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
      setFromWeekStart(getWeekStartFromDate(fourWeeksAgo.toISOString().split('T')[0]));
    }
  }, [weekStartsOn, fromWeekStart, getCurrentWeekStart, getWeekStartFromDate]);

  const handleWeekChange = (type: 'from' | 'to', direction: 'prev' | 'next') => {
    const currentDate = type === 'from' ? new Date(fromWeekStart) : new Date(toWeekStart);
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));

    if (type === 'from') {
      setFromWeekStart(newDate.toISOString().split('T')[0]);
    } else {
      setToWeekStart(newDate.toISOString().split('T')[0]);
    }
  };

  const handleDateChange = (type: 'from' | 'to', dateString: string) => {
    const weekStart = getWeekStartFromDate(dateString);
    if (type === 'from') {
      setFromWeekStart(weekStart);
    } else {
      setToWeekStart(weekStart);
    }
  };

  const isValidRange =
    !!fromWeekStart && !!toWeekStart && new Date(fromWeekStart) <= new Date(toWeekStart);

  const buildParameters = useCallback((): TimecardAnalysisFilterParams => {
    return {
      fromWeekStartDate: fromWeekStart,
      toWeekStartDate: toWeekStart,
      ...buildCommonFilters(),
    };
  }, [fromWeekStart, toWeekStart, buildCommonFilters]);

  const handleGenerateReport = () => {
    if (!isValidRange) return;
    onGenerateReport({
      reportType: 'timecards-analysis',
      parameters: buildParameters(),
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900">Timecards Analysis</h2>
        <p className="text-sm text-neutral-600 mt-1">
          View detailed timecard data across a week range with optional schedule, position, and employee filters
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-3">
                <Calendar className="inline h-4 w-4 mr-2" />
                Week Start
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleWeekChange('from', 'prev')}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  ←
                </button>
                <div className="flex-1 min-w-0">
                  <input
                    type="date"
                    value={fromWeekStart}
                    onChange={(e) => handleDateChange('from', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm"
                  />
                  <div className="text-xs text-neutral-600 mt-1">
                    {fromWeekStart && formatWeekRangeLabel(fromWeekStart)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleWeekChange('from', 'next')}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  →
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-3">
                <Calendar className="inline h-4 w-4 mr-2" />
                Week End
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleWeekChange('to', 'prev')}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  ←
                </button>
                <div className="flex-1 min-w-0">
                  <input
                    type="date"
                    value={toWeekStart}
                    onChange={(e) => handleDateChange('to', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm"
                  />
                  <div className="text-xs text-neutral-600 mt-1">
                    {toWeekStart && formatWeekRangeLabel(toWeekStart)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleWeekChange('to', 'next')}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  →
                </button>
              </div>
            </div>
          </div>

          {!isValidRange && fromWeekStart && toWeekStart && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-900">Week Start must be before or equal to Week End</p>
              </div>
            </div>
          )}

          <TimecardReportFiltersPanel
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            employees={employees}
            schedules={schedules}
            positions={positions}
            selectedEmployeeIds={selectedEmployeeIds}
            onEmployeeIdsChange={setSelectedEmployeeIds}
            selectedScheduleIds={selectedScheduleIds}
            onScheduleIdsChange={setSelectedScheduleIds}
            selectedPositionIds={selectedPositionIds}
            onPositionIdsChange={setSelectedPositionIds}
            loadingOptions={loadingOptions}
          />

          <div className="pt-1">
            <button
              type="button"
              onClick={handleGenerateReport}
              disabled={!isValidRange}
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              <Play className="h-4 w-4" />
              Generate Report
            </button>
          </div>
        </div>

        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 h-fit">
          <h3 className="text-sm font-semibold text-neutral-900 mb-3">Report Includes</h3>
          <div className="text-sm text-neutral-700 space-y-1.5">
            <div>• Employee Name</div>
            <div>• Date</div>
            <div>• Scheduled/Actual/Paid Times</div>
            <div>• Hours (Scheduled/Actual/Paid)</div>
            <div>• Break Minutes</div>
            <div>• Schedule &amp; Position</div>
            <div>• Approval Status</div>
          </div>
          <div className="mt-3 pt-3 border-t border-neutral-300">
            <p className="text-xs text-neutral-600">
              <span className="font-medium">Note:</span> Use the timecard status filter to include approved,
              unapproved, or all timecards. Leave other filters empty to include all.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimecardsAnalysis;
